import React from 'react';
import type { NodeInstance } from '@glueapp/graph-types';
import type { AgentInstance } from './agentTypes';

interface Props {
    agent: AgentInstance;
    nodeInstances: NodeInstance[];
}

/**
 * SVG tapered line from agent panel to the spotlight node.
 * Uses a filled path with two parallel cubic Bezier curves
 * offset by varying width (wider at endpoints, narrower at midpoint).
 */
export const AgentConnectionLine: React.FC<Props> = ({ agent, nodeInstances }) => {
    if (agent.activeNodeId === null) return null;

    const targetNode = nodeInstances.find(node => node.id === agent.activeNodeId);
    if (!targetNode) return null;

    // Agent panel bottom-center
    const panelWidth = agent.isExpanded ? 400 : 280;
    const panelBottomCenter = {
        x: agent.position.x + panelWidth / 2,
        y: agent.position.y + 40, // approximate header height
    };

    // Target node center
    const nodeCenter = {
        x: targetNode.position.x + (targetNode.width || 200) / 2,
        y: targetNode.position.y + (targetNode.height || 100) / 2,
    };

    // Direction vector
    const deltaX = nodeCenter.x - panelBottomCenter.x;
    const deltaY = nodeCenter.y - panelBottomCenter.y;
    const length = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    if (length < 10) return null;

    // Normal vector (perpendicular to direction)
    const normalX = -deltaY / length;
    const normalY = deltaX / length;

    // Width at endpoints vs midpoint
    const endpointHalfWidth = 3;
    const midpointHalfWidth = 1;

    // Start and end points
    const startX = panelBottomCenter.x;
    const startY = panelBottomCenter.y;
    const endX = nodeCenter.x;
    const endY = nodeCenter.y;

    // Midpoint
    const midX = (startX + endX) / 2;
    const midY = (startY + endY) / 2;

    // Control points for the Bezier (use midpoint-ish)
    const controlOffsetFraction = 0.3;
    const control1X = startX + deltaX * controlOffsetFraction;
    const control1Y = startY + deltaY * controlOffsetFraction;
    const control2X = startX + deltaX * (1 - controlOffsetFraction);
    const control2Y = startY + deltaY * (1 - controlOffsetFraction);

    // Build the tapered path: top edge forward, bottom edge backward
    const pathData = [
        // Start top
        `M ${startX + normalX * endpointHalfWidth} ${startY + normalY * endpointHalfWidth}`,
        // Curve to midpoint top (narrow)
        `Q ${control1X + normalX * midpointHalfWidth} ${control1Y + normalY * midpointHalfWidth}`,
        `${midX + normalX * midpointHalfWidth} ${midY + normalY * midpointHalfWidth}`,
        // Curve to end top
        `Q ${control2X + normalX * midpointHalfWidth} ${control2Y + normalY * midpointHalfWidth}`,
        `${endX + normalX * endpointHalfWidth} ${endY + normalY * endpointHalfWidth}`,
        // End bottom (return path)
        `L ${endX - normalX * endpointHalfWidth} ${endY - normalY * endpointHalfWidth}`,
        // Curve back to midpoint bottom
        `Q ${control2X - normalX * midpointHalfWidth} ${control2Y - normalY * midpointHalfWidth}`,
        `${midX - normalX * midpointHalfWidth} ${midY - normalY * midpointHalfWidth}`,
        // Curve to start bottom
        `Q ${control1X - normalX * midpointHalfWidth} ${control1Y - normalY * midpointHalfWidth}`,
        `${startX - normalX * endpointHalfWidth} ${startY - normalY * endpointHalfWidth}`,
        'Z',
    ].join(' ');

    return (
        <path
            d={pathData}
            fill={agent.color.gradient}
            fillOpacity={0.25}
            stroke={agent.color.gradient}
            strokeOpacity={0.4}
            strokeWidth={0.5}
        />
    );
};
